<template>
    <ProgressBar
        v-bind="passthroughAttrs"
        :value="progressValue"
        :mode="progressMode"
        :class="progressClasses"
    >
        <template v-if="showValue && progressMode !== 'indeterminate'">
            {{ progressValue }}%
        </template>
        <slot v-else />
    </ProgressBar>
</template>

<script>
import { getPrimeVueSeverity } from './utils/componentMappings.js';
/**
 * EProgress - Abstraction wrapper around PrimeVue's ProgressBar
 *
 * Usage:
 *   <e-progress :value="45" show-value>
 *   </e-progress>
 *
 *   <e-progress :value="progress" :max="100" type="is-success">
 *   </e-progress>
 *
 *   <e-progress :value="75" type="is-warning" show-value format="percent">
 *   </e-progress>
 *
 *   <e-progress :value="downloadProgress" size="is-large">
 *     Downloading...
 *   </e-progress>
 *
 *   <e-progress indeterminate size="is-small" type="is-primary">
 *   </e-progress>
 *
 * Props (work in both modes):
 *   - value: Number - Progress value (0-100 or 0-max)
 *   - max: Number - Maximum value (default: 100)
 *   - type: String - Color type (is-primary, is-success, is-danger, etc.)
 *   - size: String - Size (is-small, is-medium, is-large)
 *   - show-value: Boolean - Show the percentage value
 *   - indeterminate: Boolean - Show indeterminate/loading state
 *
 * All Buefy b-progress props, events, and slots are supported in Buefy mode.
 */
export default {
    name: 'EProgress',
    inheritAttrs: false,
    props: {
        value: {
            type: Number,
            default: undefined,
        },
        max: {
            type: Number,
            default: 100,
        },
        type: {
            type: String,
            default: undefined,
        },
        size: {
            type: String,
            default: undefined,
        },
        showValue: {
            type: Boolean,
            default: false,
        },
        indeterminate: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        progressValue() {
            // If value is undefined or indeterminate, return 0 for PrimeVue
            if (this.value === undefined || this.indeterminate) {
                return 0;
            }
            // Convert value to percentage if max !== 100
            if (this.max !== 100 && this.max > 0) {
                return Math.round((this.value / this.max) * 100);
            }
            return this.value;
        },
        progressMode() {
            // If value is undefined or indeterminate prop is true, use indeterminate mode
            if (this.value === undefined || this.indeterminate) {
                return 'indeterminate';
            }
            return 'determinate';
        },
        progressClasses() {
            const classes = ['e-progress-bar'];

            // Add size classes
            if (this.size) {
                const sizeMap = {
                    'is-small': 'e-progress-small',
                    'is-medium': 'e-progress-medium',
                    'is-large': 'e-progress-large',
                };
                if (sizeMap[this.size]) {
                    classes.push(sizeMap[this.size]);
                }
            }

            // Add type/severity classes
            if (this.type) {
                const severity = getPrimeVueSeverity(this.type);
                classes.push(`e-progress-${severity}`);
            }

            return classes.join(' ');
        },
        passthroughAttrs() {
            // Filter out our custom props that don't apply to PrimeVue
            const { value, max, type, size, showValue, indeterminate, ...rest } = this.$attrs;
            return rest;
        },
    },
};
</script>

<style>
/* Override PrimeVue CSS variables for theming */
.e-progress-bar {
    --p-progressbar-height: 1.25rem;
}

/* Size variations */
.e-progress-small {
    --p-progressbar-height: 0.75rem;
}

.e-progress-medium {
    --p-progressbar-height: 1.5rem;
}

.e-progress-large {
    --p-progressbar-height: 2rem;
}

/* Color/severity variations */
.e-progress-primary {
    --p-progressbar-value-background: var(--primary-color, var(--p-primary-color, #3273dc));
}

.e-progress-success {
    --p-progressbar-value-background: #48c774;
}

.e-progress-danger {
    --p-progressbar-value-background: #f14668;
}

.e-progress-warn {
    --p-progressbar-value-background: #ffdd57;
}

.e-progress-info {
    --p-progressbar-value-background: #3298dc;
}

.e-progress-secondary {
    --p-progressbar-value-background: #363636;
}

.e-progress-contrast {
    --p-progressbar-value-background: #f5f5f5;
}
</style>
